import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import StudioShell from "@/components/StudioShell";
import { Plus, ArrowRight } from "lucide-react";

const STATUS_COLORS = {
  "Questionnaire": "#9E9E98",
  "Analysing": "#8C6D46",
  "Design Direction Ready": "#5A6553",
  "Generating": "#B35A38",
  "Ready for Review": "#5A6553",
  "Completed": "#1A1A18",
};

const THUMBS = [
  "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  "https://images.unsplash.com/photo-1783685158053-08213c96da9a?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  "https://images.unsplash.com/photo-1763485956228-5106a622ab47?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  "https://images.unsplash.com/photo-1763485956292-6fb531f01b0c?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
];

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data)).catch(e => toast.error(apiError(e, "Could not load projects"))).finally(() => setLoading(false));
  }, []);

  return (
    <StudioShell>
      <div className="px-6 sm:px-12 py-12 max-w-[1440px] mx-auto">
        <div className="flex flex-wrap items-end justify-between mb-12 gap-4">
          <div>
            <div className="eyebrow mb-3">Studio Workspace</div>
            <h1 className="editorial-heading text-4xl sm:text-5xl">Recent Projects</h1>
          </div>
          <Link to="/project/new" data-testid="dashboard-new-project-btn" className="inline-flex items-center gap-3 px-6 py-4 bg-black text-white text-sm">
            <Plus size={16} /> Create New Project
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {[
            ["Active Projects", projects.length],
            ["In Analysis", projects.filter(p => p.status === "Analysing" || p.status === "Generating").length],
            ["Completed", projects.filter(p => p.status === "Completed").length],
          ].map(([label, n]) => (
            <div key={label} className="studio-card p-8">
              <div className="eyebrow mb-3">{label}</div>
              <div className="font-serif-editorial text-5xl">{n}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-stone-500 text-sm">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="studio-card p-16 text-center">
            <div className="eyebrow mb-4">No projects yet</div>
            <h2 className="editorial-heading text-3xl mb-6">Begin your first design monograph.</h2>
            <Link to="/project/new" data-testid="dashboard-empty-cta" className="inline-flex items-center gap-3 px-6 py-3 bg-black text-white text-sm">
              Create New Project <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((p, i) => (
              <Link key={p.id} to={`/project/${p.id}/analysis`} data-testid={`project-card-${p.id}`} className="studio-card group overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                  <img src={THUMBS[i % THUMBS.length]} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="eyebrow">{p.discipline ? `${p.discipline} · ` : ""}{p.project_type}</span>
                    <span className="text-[11px] font-mono-lab" style={{ color: STATUS_COLORS[p.status] || "#686864" }}>● {p.status}</span>
                  </div>
                  <h3 className="font-serif-editorial text-2xl mb-1">{p.name}</h3>
                  <div className="text-sm text-stone-500">{p.client_name}</div>
                  <div className="hairline my-4" />
                  <div className="flex justify-between text-xs text-stone-500">
                    <span>{p.location || "—"}</span>
                    <span>{new Date(p.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </StudioShell>
  );
}
